import { CardNumberElement, Elements, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { createUseStyles, useTheme } from 'react-jss'
import style from './style'
import { TypographyElement, Button, Skeleton, ButtonVariation, ButtonSize } from 'UI'
import Text from './text.json'
import { useState, useEffect } from 'react'
import classNames from 'classnames'
import { BASE_URL_WEB, StripePublicKey } from 'constants/'
import fetch from 'core/fetch'

const useStyle = createUseStyles(style)
const stripePromise = loadStripe(StripePublicKey)

const TextErrors = validField => {
  switch (true) {
    case !validField.encryptedCardNumber?.empty && validField.encryptedCardNumber?.message !== undefined:
      return Text.encryptedCardNumberInvalid
    case !validField.encryptedExpiryDate?.empty && validField.encryptedExpiryDate?.message !== undefined:
      return Text.encryptedExpiryMonthInvalid
    case !validField.encryptedSecurityCode?.empty && validField.encryptedSecurityCode?.message !== undefined:
      return Text.encryptedSecurityCodeInvalid
    default:
      return null
  }
}

const CardInfosForm = ({ addCreditCard, closeForm, clientSecret }) => {
  const theme = useTheme()
  const stripe = useStripe()
  const elements = useElements()
  const [validFields, setValidFields] = useState({
    encryptedCardNumber: null,
    encryptedExpiryDate: null,
    encryptedSecurityCode: null
  })
  const [loading3ds, setLoading3ds] = useState(false)
  const {
    frameClass,
    creditCardGroup,
    encryptedCardNumber,
    encryptedExpiryDate,
    encryptedSecurityCode,
    formAndButton,
    encryptedCardNumberExtraClass,
    encryptedExpiryDateExtraClass,
    encryptedSecurityCodeExtraClass
  } = useStyle({ encryptedCardNumber: validFields.encryptedCardNumber, encryptedExpiryDate: validFields.encryptedExpiryDate, encryptedSecurityCode: validFields.encryptedSecurityCode })

  const handleSubmit = async event => {
    event.preventDefault()
    setLoading3ds(true)
    const { setupIntent } = await stripe.confirmCardSetup(clientSecret, { payment_method: { card: elements.getElement(CardNumberElement) } })
    addCreditCard(setupIntent?.payment_method, closeForm)
    setLoading3ds(false)
  }

  const errorText = TextErrors(validFields)

  return (
    <form
      className={formAndButton}
      onSubmit={handleSubmit}
    >
      <div className={creditCardGroup}>
        <div className={encryptedCardNumber}>
          <TypographyElement
            variant='label'
            spacing='0 0 8px 0'
          >{Text.labels.cardNumber}
          </TypographyElement>
          <CardNumberElement
            className={classNames(frameClass, encryptedCardNumberExtraClass)}
            onChange={(e) => setValidFields({ ...validFields, encryptedCardNumber: { message: e?.error?.message, empty: e?.empty } })}
            options={{ showIcon: true }}
          />
        </div>
        <div className={encryptedExpiryDate}>
          <TypographyElement
            variant='label'
            spacing='0 0 8px 0'
          >{Text.labels.expiration}
          </TypographyElement>
          <CardExpiryElement
            className={classNames(frameClass, encryptedExpiryDateExtraClass)}
            onChange={(e) => setValidFields({ ...validFields, encryptedExpiryDate: { message: e?.error?.message, empty: e?.empty } })}
          />
        </div>
        <div className={encryptedSecurityCode}>
          <TypographyElement
            variant='label'
            spacing='0 0 8px 0'
          >{Text.labels.cardCode}
          </TypographyElement>
          <CardCvcElement
            className={classNames(frameClass, encryptedSecurityCodeExtraClass)}
            onChange={(e) => setValidFields({ ...validFields, encryptedSecurityCode: { message: e?.error?.message, empty: e?.empty } })}
          />
        </div>
      </div>
      {
        errorText
          ? (
            <TypographyElement
              variant='label'
              color={theme.error[0]}
              spacing='16px 0 0 0'
            >
              {errorText}
            </TypographyElement>
            )
          : null
    }
      <Button
        dataTest='add-credit-card-button'
        width='100%'
        type='submit'
        label={Text.addCardButton}
        marginButton='24px 0 0 0'
        variation={ButtonVariation.primary}
        size={ButtonSize.small}
        disabled={Object.values(validFields).some(field => field?.message !== undefined || field?.empty || field === null) || loading3ds}
      />
    </form>
  )
}

const setupQuery = `${BASE_URL_WEB}/Payment/creditCard/setup`

export default ({ addCreditCard, closeForm, addingCreditCard }) => {
  const [clientSecret, setClientSecret] = useState(null)
  const options = {
    clientSecret: clientSecret,
    locale: 'fr'
  }
  useEffect(() => {
    const fetchSetup = async () => {
      const data = await fetch(setupQuery, { method: 'POST' })
      setClientSecret(data)
    }
    fetchSetup()
  }, [])

  return (
    !addingCreditCard && clientSecret
      ? (
        <Elements stripe={stripePromise} options={options}>
          <CardInfosForm addCreditCard={addCreditCard} closeForm={closeForm} clientSecret={clientSecret} />
        </Elements>
        )
      : <Skeleton lines={2} height={[63, 50]} width='100%' margin='0 0 16px 0' />
  )
}
